import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useState, useCallback } from "react";
import {
  ArrowLeft,
  Plus,
  Edit,
  Trash2,
  Eye,
  Upload,
  Search,
  Filter,
  Grid,
  List,
  Calendar,
  User,
  CloudUpload,
} from "lucide-react";
import {
  createNews,
  deleteNews,
  fetchNews,
  IMG_BASE_URL,
  updateNews,
} from "./hooks/hooks";
import * as Yup from "yup";

// Validation functions
const validateCreateForm = (values) => {
  const errors = {};
  if (!values.title || values.title.trim() === "") {
    errors.title = "Title is required";
  } else if (values.title.length > 100) {
    errors.title = "Title must be at most 100 characters";
  }

  if (!values.content || values.content.trim() === "") {
    errors.content = "Content is required";
  } else if (values.content.length < 50) {
    errors.content = "Content must be at least 50 characters";
  }

  if (!values.image) {
    errors.image = "Image is required";
  } else if (
    values.image &&
    !["image/jpeg", "image/png", "image/gif"].includes(values.image.type)
  ) {
    errors.image = "Only image files are allowed";
  }

  return errors;
};

const validateUpdateForm = (values) => {
  const errors = {};
  if (!values.title || values.title.trim() === "") {
    errors.title = "Title is required";
  } else if (values.title.length > 100) {
    errors.title = "Title must be at most 100 characters";
  }

  if (!values.content || values.content.trim() === "") {
    errors.content = "Content is required";
  } else if (values.content.length < 50) {
    errors.content = "Content must be at least 50 characters";
  }

  return errors;
};

// Toast Component
const Toast = ({ isOpen, message, type, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed top-4 right-4 z-50">
      <div
        className={`px-6 py-4 rounded-lg shadow-lg ${
          type === "success" ? "bg-green-500" : "bg-red-500"
        } text-white`}
      >
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">{message}</span>
          <button
            onClick={onClose}
            className="ml-4 text-white hover:text-gray-200"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
};

// News Detail Component
const NewsDetail = ({ item }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden max-w-4xl mx-auto">
    {item?.image && (
      <div className="aspect-video">
        <img
          src={`${IMG_BASE_URL}${item.image}`}
          alt={item?.title}
          className="w-full h-full object-cover"
        />
      </div>
    )}

    <div className="p-8">
      <div className="mb-4">
        <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
          {new Date(item?.published_at).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </span>
      </div>

      <h1 className="text-3xl font-bold text-blue-900 mb-6">{item?.title}</h1>

      <div className="prose prose-lg max-w-none">
        <p className="text-gray-700 leading-relaxed whitespace-pre-line">
          {item?.content}
        </p>
      </div>

      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="flex items-center text-sm text-gray-500">
          <User className="w-4 h-4 mr-1" />
          <span>By {item?.author}</span>
          <Calendar className="w-4 h-4 ml-4 mr-1" />
          <span>{new Date(item?.published_at).toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  </div>
);

function News() {
  const queryClient = useQueryClient();
  const {
    data: news,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["news"],
    queryFn: fetchNews,
  });

  const [selectedItem, setSelectedItem] = useState(null);
  const [open, setOpen] = useState({
    create: false,
    update: false,
    detail: false,
  });
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [viewMode, setViewMode] = useState("table");

  // Create form state
  const [createFormData, setCreateFormData] = useState({
    title: "",
    content: "",
    image: null,
  });
  const [createFormErrors, setCreateFormErrors] = useState({});
  const [createFormTouched, setCreateFormTouched] = useState({});

  // Update form state
  const [updateFormData, setUpdateFormData] = useState({
    title: "",
    content: "",
    image: null,
  });
  const [updateFormErrors, setUpdateFormErrors] = useState({});
  const [updateFormTouched, setUpdateFormTouched] = useState({});

  // Ensure news is an array before filtering
  const filteredNews = Array.isArray(news)
    ? news.filter((item) =>
        item.title?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  // Sort news dynamically
  const sortedNews = [...filteredNews].sort((a, b) => {
    if (sortBy === "title") {
      return a.title.localeCompare(b.title);
    } else {
      return new Date(b.published_at) - new Date(a.published_at);
    }
  });

  // Handle Pagination
  const handleChangePage = (newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (value) => {
    setRowsPerPage(parseInt(value, 10));
    setPage(0);
  };

  // Create News Mutation
  const mutation = useMutation({
    mutationFn: createNews,
    onSuccess: (response) => {
      console.log("Create success:", response);
      queryClient.invalidateQueries({ queryKey: ["newsPost"] });
      queryClient.invalidateQueries({ queryKey: ["news"] });
      setCreateFormData({ title: "", content: "", image: null });
      setCreateFormErrors({});
      setCreateFormTouched({});
      setOpen({ create: false, update: false, detail: false });
      setToast({
        open: true,
        message: "News added successfully!",
        severity: "success",
      });
      setTimeout(
        () => setToast({ open: false, message: "", severity: "" }),
        3000
      );
      refetch();
    },
    onError: (error) => {
      console.error("Create error:", error);
      setToast({
        open: true,
        message: "Failed to add news. Try again.",
        severity: "error",
      });
      setTimeout(
        () => setToast({ open: false, message: "", severity: "" }),
        3000
      );
    },
  });

  // Update News Mutation
  const updateMutation = useMutation({
    mutationFn: updateNews,
    onSuccess: () => {
      console.log("Update success");
      queryClient.invalidateQueries(["newsUpdate"]);
      queryClient.invalidateQueries({ queryKey: ["news"] });
      setUpdateFormData({ title: "", content: "", image: null });
      setUpdateFormErrors({});
      setUpdateFormTouched({});
      setOpen({ create: false, update: false, detail: false });
      setToast({
        open: true,
        message: "News updated successfully!",
        severity: "success",
      });
      setTimeout(
        () => setToast({ open: false, message: "", severity: "" }),
        3000
      );
      refetch();
    },
    onError: (error) => {
      console.error("Update error:", error);
      setToast({
        open: true,
        message: "Failed to update news. Try again.",
        severity: "error",
      });
      setTimeout(
        () => setToast({ open: false, message: "", severity: "" }),
        3000
      );
    },
  });

  // Delete News Mutation
  const deleteMutation = useMutation({
    mutationFn: deleteNews,
    onSuccess: () => {
      queryClient.invalidateQueries(["newsDelete"]);
      queryClient.invalidateQueries({ queryKey: ["news"] });
      setToast({
        open: true,
        message: "News deleted successfully!",
        severity: "success",
      });
      setTimeout(
        () => setToast({ open: false, message: "", severity: "" }),
        3000
      );
      refetch();
    },
    onError: (error) => {
      setToast({
        open: true,
        message: "Failed to delete news. Try again.",
        severity: "error",
      });
      setTimeout(
        () => setToast({ open: false, message: "", severity: "" }),
        3000
      );
    },
  });

  // Form handlers - simple functions without useCallback
  const handleCreateFormChange = (e) => {
    const { name, value } = e.target;
    setCreateFormData(prev => ({ ...prev, [name]: value }));
    if (createFormErrors[name]) {
      setCreateFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleUpdateFormChange = (e) => {
    const { name, value } = e.target;
    setUpdateFormData(prev => ({ ...prev, [name]: value }));
    if (updateFormErrors[name]) {
      setUpdateFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCreateFormBlur = (e) => {
    const { name } = e.target;
    setCreateFormTouched((prev) => ({ ...prev, [name]: true }));
  };

  const handleCreateFileChange = (e) => {
    const file = e.target.files[0];
    setCreateFormData((prev) => ({ ...prev, image: file }));
    setCreateFormTouched((prev) => ({ ...prev, image: true }));
  };

  const handleCreateFormSubmit = (e) => {
    e.preventDefault();
    console.log("Create form submitted:", createFormData);

    const errors = validateCreateForm(createFormData);
    setCreateFormErrors(errors);
    setCreateFormTouched({ title: true, content: true, image: true });

    if (Object.keys(errors).length === 0) {
      mutation.mutate(createFormData);
    }
  };

  const handleUpdateFormBlur = (e) => {
    const { name } = e.target;
    setUpdateFormTouched((prev) => ({ ...prev, [name]: true }));
  };

  const handleUpdateFileChange = (e) => {
    const file = e.target.files[0];
    setUpdateFormData((prev) => ({ ...prev, image: file }));
    setUpdateFormTouched((prev) => ({ ...prev, image: true }));
  };

  const handleUpdateFormSubmit = (e) => {
    e.preventDefault();
    console.log("Update form submitted:", updateFormData);

    const errors = validateUpdateForm(updateFormData);
    setUpdateFormErrors(errors);
    setUpdateFormTouched({ title: true, content: true });

    if (Object.keys(errors).length === 0 && selectedItem?.id) {
      updateMutation.mutate({ id: selectedItem.id, data: updateFormData });
    }
  };

  // Open drawer and set form data when updating
  const handleOpenUpdateDrawer = (item) => {
    setSelectedItem(item);
    setUpdateFormData({
      title: item.title || "",
      content: item.content || "",
      image: null,
    });
    setUpdateFormErrors({});
    setUpdateFormTouched({});
    setOpen({ create: false, update: true, detail: false });
  };

  const handleOpenDetailsDrawer = (item) => {
    setSelectedItem(item);
    setOpen({ create: false, update: false, detail: true });
  };

  // Function to toggle create drawer
  const toggleDrawer = (type, state) => () => {
    setOpen({
      create: type === "create" ? state : false,
      update: type === "update" ? state : false,
      detail: type === "detail" ? state : false,
    });

    // Reset forms when closing
    if (!state) {
      if (type === "create") {
        setCreateFormData({ title: "", content: "", image: null });
        setCreateFormErrors({});
        setCreateFormTouched({});
      } else if (type === "update") {
        setUpdateFormData({ title: "", content: "", image: null });
        setUpdateFormErrors({});
        setUpdateFormTouched({});
      }
    }
  };

  // Handle delete
  const handleDelete = (id) => {
    deleteMutation.mutate(id);
  };

  // Table View Component
  const TableView = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Article
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Author
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Published Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedNews
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      {item.image && (
                        <div className="flex-shrink-0 h-12 w-12 mr-4">
                          <img
                            className="h-12 w-12 rounded-lg object-cover"
                            src={`${IMG_BASE_URL}${item.image}`}
                            alt={item.title}
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {item.title}
                        </p>
                        <p className="text-sm text-gray-500 truncate">
                          {item.content.substring(0, 80)}...
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{item.author}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {new Date(item.published_at).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleOpenDetailsDrawer(item)}
                        className="p-2 text-gray-500 hover:text-blue-600 transition-colors"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleOpenUpdateDrawer(item)}
                        className="p-2 text-gray-500 hover:text-blue-600 transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="p-2 text-gray-500 hover:text-red-600 transition-colors"
                        title="Delete"
                        disabled={deleteMutation.isLoading}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Grid View Component
  const GridView = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
      {sortedNews
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map((item) => (
          <div
            key={item.id}
            className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
          >
            {item.image && (
              <div className="aspect-video">
                <img
                  src={`${IMG_BASE_URL}${item.image}`}
                  alt={item.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div className="p-6">
              <div className="mb-3">
                <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded">
                  {new Date(item.published_at).toLocaleDateString()}
                </span>
              </div>

              <h3 className="text-lg font-bold text-gray-900 mb-3 line-clamp-2">
                {item.title}
              </h3>

              <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                {item.content.substring(0, 120)}...
              </p>

              <div className="flex items-center justify-between">
                <button
                  onClick={() => handleOpenDetailsDrawer(item)}
                  className="text-blue-600 hover:text-blue-800 text-sm font-semibold"
                >
                  Read More
                </button>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleOpenDetailsDrawer(item)}
                    className="p-2 text-gray-500 hover:text-blue-600 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleOpenUpdateDrawer(item)}
                    className="p-2 text-gray-500 hover:text-blue-600 transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="p-2 text-gray-500 hover:text-red-600 transition-colors"
                    disabled={deleteMutation.isLoading}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center mt-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex justify-center items-center mt-20">
        <p className="text-red-600 text-sm">
          Error fetching news. Please try again later.
        </p>
      </div>
    );
  }

  // Main render logic
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Toast Notification */}
        <Toast
          isOpen={toast.open}
          message={toast.message}
          type={toast.severity}
          onClose={() => setToast({ open: false, message: "", severity: "" })}
        />

        {/* CREATE FORM */}
        {open.create && (
          <div>
            <button
              onClick={toggleDrawer("create", false)}
              className="flex items-center text-gray-600 hover:text-blue-600 mb-6 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to News List
            </button>
            
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
              <div className="xl:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-8">
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-blue-900 mb-2">
                    Add New Blog Post
                  </h2>
                  <p className="text-gray-600">
                    Share the latest news and insights with your audience
                  </p>
                </div>

                <form onSubmit={handleCreateFormSubmit}>
                  <div className="space-y-6">
                    <div>
                      <label
                        htmlFor="create-title"
                        className="block text-sm font-semibold text-gray-700 mb-2"
                      >
                        Title *
                      </label>
                      <input
                        id="create-title"
                        type="text"
                        name="title"
                        value={createFormData.title}
                        onChange={handleCreateFormChange}
                        onBlur={handleCreateFormBlur}
                        className={`w-full px-4 py-3 border rounded-lg transition-colors ${
                          createFormTouched.title && createFormErrors.title ? 'border-red-500' : 'border-gray-300'
                        }`}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#001f3f';
                          e.target.style.boxShadow = `0 0 0 2px rgba(0, 31, 63, 0.2)`;
                        }}
                       
                        placeholder="Enter blog title..."
                      />
                      {createFormTouched.title && createFormErrors.title && (
                        <p className="mt-1 text-sm text-red-600">
                          {createFormErrors.title}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="create-content"
                        className="block text-sm font-semibold text-gray-700 mb-2"
                      >
                        Content *
                      </label>
                      <textarea
                        id="create-content"
                        name="content"
                        rows={6}
                        value={createFormData.content}
                        onChange={handleCreateFormChange}
                        className={`w-full px-4 py-3 border rounded-lg transition-colors resize-none ${
                          updateFormTouched.content && updateFormErrors.content
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        style={{ 
                          focusRingColor: '#001f3f',
                          focusBorderColor: '#001f3f' 
                        }}
                        onBlur={(e) => {
                          handleCreateFormBlur(e);
                          if (!createFormErrors.content) {
                            e.target.style.borderColor = '#d1d5db';
                            e.target.style.boxShadow = 'none';
                          }
                        }}
                        placeholder="Write your blog content here..."
                      />
                      {createFormTouched.content && createFormErrors.content && (
                        <p className="mt-1 text-sm text-red-600">
                          {createFormErrors.content}
                        </p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        {createFormData.content.length}/50 characters minimum
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={mutation.isLoading}
                      className="w-full text-white py-3 px-6 rounded-lg font-semibold hover:opacity-90 transition-colors focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ backgroundColor: '#001f3f', focusRingColor: '#001f3f' }}
                    >
                      {mutation.isLoading ? "Publishing..." : "Publish Blog Post"}
                    </button>
                  </div>
                </form>
              </div>

              <div className="xl:col-span-1 bg-white rounded-xl shadow-sm border border-gray-100 p-8">
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    Featured Image
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Upload an image to make your post more engaging
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <input
                      type="file"
                      name="image"
                      onChange={handleCreateFileChange}
                      onBlur={handleCreateFormBlur}
                      accept="image/*"
                      className="hidden"
                      id="create-image-upload"
                    />
                    <label
                      htmlFor="create-image-upload"
                      className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-gray-50 transition-colors"
                    >
                      <CloudUpload className="w-10 h-10 text-gray-400 mb-3" />
                      <span className="text-sm font-medium text-gray-700 mb-1">
                        Click to upload image
                      </span>
                      <span className="text-xs text-gray-500">
                        JPEG, PNG or GIF (Max 5MB)
                      </span>
                    </label>
                  </div>

                  {createFormTouched.image && createFormErrors.image && (
                    <p className="text-sm text-red-600">{createFormErrors.image}</p>
                  )}

                  {createFormData.image && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-3">
                        Selected: {createFormData.image.name}
                      </p>
                      <img
                        src={URL.createObjectURL(createFormData.image)}
                        alt="Preview"
                        className="w-full h-64 object-cover rounded-lg shadow-sm"
                      />
                    </div>
                  )}

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-blue-800 mb-2">
                      Image Tips:
                    </h4>
                    <ul className="text-xs text-blue-700 space-y-1">
                      <li>• Use high-quality images (min 800x600px)</li>
                      <li>• Optimal aspect ratio: 16:9 for best display</li>
                      <li>• Keep file size under 5MB for faster loading</li>
                      <li>• Use relevant images that support your content</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* UPDATE FORM */}
        {open.update && (
          <div>
            <button
              onClick={toggleDrawer("update", false)}
              className="flex items-center text-gray-600 hover:text-blue-600 mb-6 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to News List
            </button>
            
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
              <div className="xl:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-8">
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-blue-900 mb-2">
                    Update Blog Post
                  </h2>
                  <p className="text-gray-600">
                    Update your blog post with the latest information
                  </p>
                </div>

                <form onSubmit={handleUpdateFormSubmit}>
                  <div className="space-y-6">
                    <div>
                      <label
                        htmlFor="update-title"
                        className="block text-sm font-semibold text-gray-700 mb-2"
                      >
                        Title *
                      </label>
                      <input
                        id="update-title"
                        type="text"
                        name="title"
                        value={updateFormData.title}
                        onChange={handleUpdateFormChange}
                        onBlur={handleUpdateFormBlur}
                        className={`w-full px-4 py-3 border rounded-lg transition-colors ${
                          updateFormTouched.title && updateFormErrors.title
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#001f3f';
                          e.target.style.boxShadow = `0 0 0 2px rgba(0, 31, 63, 0.2)`;
                        }}
                        
                        placeholder="Enter blog title..."
                      />
                      {updateFormTouched.title && updateFormErrors.title && (
                        <p className="mt-1 text-sm text-red-600">
                          {updateFormErrors.title}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="update-content"
                        className="block text-sm font-semibold text-gray-700 mb-2"
                      >
                        Content *
                      </label>
                      <textarea
                        id="update-content"
                        name="content"
                        rows={6}
                        value={updateFormData.content}
                        onChange={handleUpdateFormChange}
                        onBlur={handleUpdateFormBlur}
                        className={`w-full px-4 py-3 border rounded-lg transition-colors resize-none ${
                          updateFormTouched.content && updateFormErrors.content
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#001f3f';
                          e.target.style.boxShadow = `0 0 0 2px rgba(0, 31, 63, 0.2)`;
                        }}
                        
                        placeholder="Write your blog content here..."
                      />
                      {updateFormTouched.content && updateFormErrors.content && (
                        <p className="mt-1 text-sm text-red-600">
                          {updateFormErrors.content}
                        </p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        {updateFormData.content.length}/50 characters minimum
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={updateMutation.isLoading}
                      className="w-full text-white py-3 px-6 rounded-lg font-semibold hover:opacity-90 transition-colors focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ backgroundColor: '#001f3f', focusRingColor: '#001f3f' }}
                    >
                      {updateMutation.isLoading ? "Updating..." : "Update Blog Post"}
                    </button>
                  </div>
                </form>
              </div>

              <div className="xl:col-span-1 bg-white rounded-xl shadow-sm border border-gray-100 p-8">
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    Featured Image
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Upload an image to make your post more engaging
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <input
                      type="file"
                      name="image"
                      onChange={handleUpdateFileChange}
                      onBlur={handleUpdateFormBlur}
                      accept="image/*"
                      className="hidden"
                      id="update-image-upload"
                    />
                    <label
                      htmlFor="update-image-upload"
                      className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-gray-50 transition-colors"
                    >
                      <CloudUpload className="w-10 h-10 text-gray-400 mb-3" />
                      <span className="text-sm font-medium text-gray-700 mb-1">
                        Click to upload image
                      </span>
                      <span className="text-xs text-gray-500">
                        JPEG, PNG or GIF (Max 5MB)
                      </span>
                    </label>
                  </div>

                  {updateFormTouched.image && updateFormErrors.image && (
                    <p className="text-sm text-red-600">{updateFormErrors.image}</p>
                  )}

                  {updateFormData.image && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-3">
                        Selected: {updateFormData.image.name}
                      </p>
                      <img
                        src={URL.createObjectURL(updateFormData.image)}
                        alt="Preview"
                        className="w-full h-64 object-cover rounded-lg shadow-sm"
                      />
                    </div>
                  )}

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-blue-800 mb-2">
                      Image Tips:
                    </h4>
                    <ul className="text-xs text-blue-700 space-y-1">
                      <li>• Use high-quality images (min 800x600px)</li>
                      <li>• Optimal aspect ratio: 16:9 for best display</li>
                      <li>• Keep file size under 5MB for faster loading</li>
                      <li>• Use relevant images that support your content</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Detail View */}
        {open.detail && (
          <div>
            <button
              onClick={toggleDrawer("detail", false)}
              className="flex items-center text-gray-600 hover:text-blue-600 mb-6 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to News List
            </button>
            <NewsDetail item={selectedItem} />
          </div>
        )}

        {/* News List */}
        {!open.create && !open.update && !open.detail && (
          <div>
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-blue-900 mb-2">
                News Management
              </h1>
              <p className="text-gray-600 mb-6">
                Manage and publish company news & updates
              </p>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <button
                  onClick={toggleDrawer("create", true)}
                  className="inline-flex items-center px-6 py-3 text-white font-semibold rounded-lg hover:opacity-90 transition-colors"
                  style={{ backgroundColor: '#001f3f' }}
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add New Blog Post
                </button>

                {/* View Mode Toggle */}
                <div className="flex items-center bg-white border border-gray-300 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode("table")}
                    className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      viewMode === "table"
                        ? "text-white"
                        : "text-gray-700 hover:opacity-70"
                    }`}
                    style={{ 
                      backgroundColor: viewMode === "table" ? '#001f3f' : 'transparent' 
                    }}
                  >
                    <List className="w-4 h-4 mr-2" />
                    Table
                  </button>
                  <button
                    onClick={() => setViewMode("grid")}
                    className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      viewMode === "grid"
                        ? "text-white"
                        : "text-gray-700 hover:opacity-70"
                    }`}
                    style={{ 
                      backgroundColor: viewMode === "grid" ? '#001f3f' : 'transparent' 
                    }}
                  >
                    <Grid className="w-4 h-4 mr-2" />
                    Grid
                  </button>
                </div>
              </div>
            </div>

            {/* Search and Filter */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search articles..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg transition-colors"
                    style={{ 
                      focusRingColor: '#001f3f',
                      focusBorderColor: '#001f3f' 
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#001f3f';
                      e.target.style.boxShadow = `0 0 0 2px rgba(0, 31, 63, 0.2)`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <div className="relative">
                  <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg transition-colors appearance-none bg-white"
                    style={{ 
                      focusRingColor: '#001f3f',
                      focusBorderColor: '#001f3f' 
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#001f3f';
                      e.target.style.boxShadow = `0 0 0 2px rgba(0, 31, 63, 0.2)`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="date">Sort by Date</option>
                    <option value="title">Sort by Title</option>
                  </select>
                </div>
              </div>
            </div>

            {/* News Display - Table or Grid */}
            <div className="mb-8">
              {viewMode === "table" ? <TableView /> : <GridView />}
            </div>

            {/* Pagination */}
            {sortedNews.length > rowsPerPage && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-700">Show</span>
                    <select
                      value={rowsPerPage}
                      onChange={(e) => handleChangeRowsPerPage(e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                    </select>
                    <span className="text-sm text-gray-700">per page</span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleChangePage(page - 1)}
                      disabled={page === 0}
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>

                    <span className="text-sm text-gray-700">
                      Page {page + 1} of{" "}
                      {Math.ceil(sortedNews.length / rowsPerPage)}
                    </span>

                    <button
                      onClick={() => handleChangePage(page + 1)}
                      disabled={
                        page >= Math.ceil(sortedNews.length / rowsPerPage) - 1
                      }
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default News;